// React hook to fetch and map form sections from backend
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SectionConfig } from '../types/sections';
import { mapProfileFields } from '../adapters/sectionMapper';
import { fetchProfileFields, fetchCountries, captureException } from '@startus/lib';

export interface UseFormSectionsReturn {
  sections: SectionConfig[] | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

// Hook to fetch dynamic form sections from backend
// Returns sections mapped to SectionConfig with empty fields
export function useFormSections(): UseFormSectionsReturn {
  const { i18n } = useTranslation();
  const [sections, setSections] = useState<SectionConfig[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loadSections = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [fields, countries] = await Promise.all([fetchProfileFields(), fetchCountries()]);
      const mapped = mapProfileFields(fields, i18n.language, countries);
      setSections(mapped);
    } catch (err) {
      captureException(err);
      setError(err);
      console.error('[useFormSections] Error loading sections:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, [i18n.language]); // Refetch when locale changes

  return {
    sections,
    isLoading,
    error,
    refetch: loadSections,
  };
}
